import * as zustand from 'zustand'
import db, { dbHelpers } from '../db/database'
import * as types from '../types'

interface AppState {
  // Data
  people: types.Person[]
  groups: types.Group[]
  selectedPeople: string[]
  viewMode: types.ViewMode
  settings: types.AppSettings

  // Loading states
  isLoading: boolean
  isSyncing: boolean
  syncProgress: Record<string, number>

  // Actions
  setPeople: (people: types.Person[]) => void
  addPerson: (person: Omit<types.Person, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updatePerson: (id: string, updates: Partial<types.Person>) => Promise<void>
  deletePerson: (id: string) => Promise<void>

  setGroups: (groups: types.Group[]) => void
  addGroup: (group: Omit<types.Group, 'id' | 'createdAt'>) => Promise<void>
  updateGroup: (id: string, updates: Partial<types.Group>) => Promise<void>
  deleteGroup: (id: string) => Promise<void>

  setSelectedPeople: (ids: string[]) => void
  togglePersonSelection: (id: string) => void
  setViewMode: (mode: types.ViewMode) => void

  syncCalendar: (calendarId: string) => Promise<void>
  syncPerson: (personId: string) => Promise<void>
  syncAll: () => Promise<void>

  loadFromDatabase: () => Promise<void>
  exportData: () => Promise<any>
  importData: (data: any) => Promise<void>
  clearAllData: () => Promise<void>
}

export const useAppStore = zustand.create<AppState>((set, get) => ({
  // Initial state
  people: [],
  groups: [],
  selectedPeople: [],
  viewMode: 'list',
  settings: {
    autoRefresh: false,
    refreshInterval: 30,
    syncOnLaunch: true,
    cacheDuration: 1,
    theme: 'auto',
    weekStartsOn: 0,
    timeFormat: '12'
  },
  isLoading: false,
  isSyncing: false,
  syncProgress: {},

  // Actions
  setPeople: (people) => set({ people }),

  addPerson: async (personData) => {
    const personId = dbHelpers.generateId()

    // Extract calendars from personData
    const { calendars: inputCalendars, ...personWithoutCalendars } = personData

    // Create person object
    const person: types.Person = {
      ...personWithoutCalendars,
      id: personId,
      calendars: [], // Will be populated after saving
      createdAt: new Date(),
      updatedAt: new Date()
    }

    // Add person to database
    await db.people.add(person)

    // Add calendars to database
    const savedCalendars: types.Calendar[] = []
    if (inputCalendars && inputCalendars.length > 0) {
      for (const calendar of inputCalendars) {
        const calendarWithPersonId = {
          ...calendar,
          personId: personId
        }
        await db.calendars.add(calendarWithPersonId)
        savedCalendars.push(calendarWithPersonId)
        console.log('Added calendar:', calendarWithPersonId)
      }
    }

    // Update person with calendars
    person.calendars = savedCalendars

    // Update state
    set((state) => ({
      people: [...state.people, person]
    }))

    console.log('Added person with calendars:', person)
  },

  updatePerson: async (id, updates) => {
    // Update database
    await db.people.update(id, updates)

    // If calendars are being updated, handle them separately
    if (updates.calendars) {
      // Delete old calendars for this person
      await db.calendars.where('personId').equals(id).delete()

      // Add new calendars
      for (const calendar of updates.calendars) {
        await db.calendars.add({
          ...calendar,
          personId: id
        })
      }
    }

    // Update state
    set((state) => ({
      people: state.people.map(p =>
        p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p
      )
    }))
  },

  deletePerson: async (id) => {
    // Delete from database
    await db.people.delete(id)
    await db.calendars.where('personId').equals(id).delete()

    // Update state
    set((state) => ({
      people: state.people.filter(p => p.id !== id),
      selectedPeople: state.selectedPeople.filter(pid => pid !== id)
    }))
  },

  setGroups: (groups) => set({ groups }),

  addGroup: async (groupData) => {
    const group: types.Group = {
      ...groupData,
      id: dbHelpers.generateId(),
      createdAt: new Date()
    }

    // Add to database
    await db.groups.add(group)

    // Update state
    set((state) => ({
      groups: [...state.groups, group]
    }))
  },

  updateGroup: async (id, updates) => {
    // Update database
    await db.groups.update(id, updates)

    // Update state
    set((state) => ({
      groups: state.groups.map(g =>
        g.id === id ? { ...g, ...updates } : g
      )
    }))
  },

  deleteGroup: async (id) => {
    // Delete from database
    await db.groups.delete(id)

    // Update state
    set((state) => ({
      groups: state.groups.filter(g => g.id !== id)
    }))
  },

  setSelectedPeople: (ids) => set({ selectedPeople: ids }),

  togglePersonSelection: (id) => {
    set((state) => ({
      selectedPeople: state.selectedPeople.includes(id)
        ? state.selectedPeople.filter(pid => pid !== id)
        : [...state.selectedPeople, id]
    }))
  },

  setViewMode: (mode) => set({ viewMode: mode }),

  syncCalendar: async (calendarId) => {
    console.log('Starting sync for calendar:', calendarId)

    set((state) => ({
      isSyncing: true,
      syncProgress: { ...state.syncProgress, [calendarId]: 0 }
    }))

    try {
      const calendar = await db.calendars.get(calendarId)
      if (!calendar) throw new Error('Calendar not found')

      console.log('Fetching availability for URL:', calendar.url)

      // Fetch availability from API
      const response = await fetch(
        `https://v1.bookeveryone.org/availabilities?url=${encodeURIComponent(calendar.url)}`
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API error:', response.status, errorText)
        throw new Error(`Failed to fetch availability: ${response.status}`)
      }

      const responseData = await response.json()
      console.log('Received API response:', responseData)

      // API wraps response in { success: true, data: [...] }
      // For profile URLs, data is an array of events
      if (!responseData.success || !responseData.data || !Array.isArray(responseData.data)) {
        throw new Error('Invalid API response structure')
      }

      const events = responseData.data as types.AvailabilityResponse[]
      console.log(`Received ${events.length} calendar(s) from API`)

      // Check if this is a profile URL with multiple calendars
      if (events.length > 1) {
        // This is a profile URL with multiple event types
        // We need to create separate calendars for each event type
        const person = get().people.find(p =>
          p.calendars.some(c => c.id === calendarId)
        )

        if (person) {
          console.log('Profile URL detected, creating calendars for each event type')

          // Remove the placeholder calendar
          await db.calendars.delete(calendarId)

          // Create new calendars for each event type
          const newCalendars: types.Calendar[] = []

          for (const eventData of events) {
            const newCalendarId = dbHelpers.generateId()
            const newCalendar: types.Calendar = {
              id: newCalendarId,
              personId: person.id,
              url: eventData.event.url || calendar.url,
              provider: 'calendly',
              name: eventData.event.name,
              enabled: true,
              lastFetchedAt: new Date(),
              cachedAvailability: {
                calendarId: newCalendarId,
                slots: eventData.availability.slots,
                fetchedAt: new Date(),
                expiresAt: new Date(Date.now() + get().settings.cacheDuration * 60 * 60 * 1000)
              }
            }

            await db.calendars.add(newCalendar)
            await db.availability.put(newCalendar.cachedAvailability!)
            newCalendars.push(newCalendar)

            console.log(`Created calendar: ${eventData.event.name} with ${eventData.availability.slots.length} slots`)
          }

          // Update state to replace the placeholder with new calendars
          set((state) => ({
            people: state.people.map(p =>
              p.id === person.id
                ? {
                    ...p,
                    calendars: [
                      ...p.calendars.filter(c => c.id !== calendarId),
                      ...newCalendars
                    ]
                  }
                : p
            )
          }))

          console.log(`Created ${newCalendars.length} calendars from profile URL`)
          return // Exit early since we handled multiple calendars
        }
      }

      // Single event handling (existing code)
      const data = events[0]
      if (!data) {
        throw new Error('No event data in response')
      }

      console.log('Single calendar extracted:', data.event.name)

      if (!data.availability || !data.availability.slots) {
        throw new Error('No availability data in response')
      }

      // Store in database
      const availability: types.Availability = {
        calendarId,
        slots: data.availability.slots,
        fetchedAt: new Date(),
        expiresAt: new Date(Date.now() + get().settings.cacheDuration * 60 * 60 * 1000)
      }

      await db.availability.put(availability)

      // Update calendar with cached availability
      await db.calendars.update(calendarId, {
        lastFetchedAt: new Date(),
        cachedAvailability: availability,
        name: data.event.name,
        error: undefined
      })

      console.log('Stored availability:', availability)

      // Update the calendar in state with new data
      set((state) => ({
        syncProgress: { ...state.syncProgress, [calendarId]: 100 },
        people: state.people.map(p => ({
          ...p,
          calendars: p.calendars.map(c =>
            c.id === calendarId
              ? { ...c, lastFetchedAt: new Date(), cachedAvailability: availability, name: data.event.name }
              : c
          )
        }))
      }))

      console.log('Sync complete for calendar:', calendarId)

    } catch (error) {
      console.error('Sync error:', error)

      await db.calendars.update(calendarId, {
        error: error instanceof Error ? error.message : 'Unknown error'
      })

      set((state) => ({
        syncProgress: { ...state.syncProgress, [calendarId]: -1 }
      }))
    } finally {
      set({ isSyncing: false })
    }
  },

  syncPerson: async (personId) => {
    const person = get().people.find(p => p.id === personId)
    if (!person) return

    for (const calendar of person.calendars) {
      await get().syncCalendar(calendar.id)
    }

    await db.people.update(personId, { lastSyncedAt: new Date() })
  },

  syncAll: async () => {
    set({ isSyncing: true, syncProgress: {} })

    for (const person of get().people) {
      await get().syncPerson(person.id)
    }

    set({ isSyncing: false })
  },

  loadFromDatabase: async () => {
    set({ isLoading: true })

    try {
      const [people, calendars, groups, settings] = await Promise.all([
        db.people.toArray(),
        db.calendars.toArray(),
        db.groups.toArray(),
        db.settings.get('default')
      ])

      console.log('Loaded from DB - People:', people.length, 'Calendars:', calendars.length)

      // Attach calendars to people
      const peopleWithCalendars = people.map(person => ({
        ...person,
        calendars: calendars.filter(c => c.personId === person.id)
      }))

      console.log('People with calendars:', peopleWithCalendars)

      set({
        people: peopleWithCalendars,
        groups,
        settings: settings || get().settings,
        isLoading: false
      })
    } catch (error) {
      console.error('Failed to load from database:', error)
      set({ isLoading: false })
    }
  },

  exportData: async () => {
    return await dbHelpers.exportData()
  },

  importData: async (data) => {
    await dbHelpers.importData(data)
    await get().loadFromDatabase()
  },

  clearAllData: async () => {
    await dbHelpers.clearAllData()
    set({
      people: [],
      groups: [],
      selectedPeople: []
    })
  }
}))

// Load data on initialization
useAppStore.getState().loadFromDatabase()